import { reactive, computed } from 'vue'
import Cookies from 'js-cookie'
import {
  FUNDRAISER_TYPES,
  DURATIONS,
  CURRENCIES,
  CURRENCY_PHONE_MAP,
  REGISTRATION_STEPS,
  AMOUNT_LIMITS,
} from '@/constants/registration'

const initialForm = () => ({
  email: '',
  title: '',
  category: '',
  currency: 'KES',
  target: '',
  duration: '',
  referralCode: '',
  fullName: '',
  idNumber: '',
  phone: '',
  birthYear: '',
})

const initialUi = () => ({
  currentStep: REGISTRATION_STEPS.STEP_1,
  showReferral: false,
  showTypeOptions: false,
  showDurationOptions: false,
  showCurrencyOptions: false,
  showPhoneCountryOptions: false,
  referralLocked: false,
  phoneMeta: {
    countryCode: CURRENCY_PHONE_MAP.KES.countryCode,
    dialCode: CURRENCY_PHONE_MAP.KES.dialCode,
    countryName: CURRENCY_PHONE_MAP.KES.countryName,
    flag: CURRENCY_PHONE_MAP.KES.flag,
    userOverrodeCountry: false,
  },
})

// shared singleton state
const form = reactive(initialForm())
const ui = reactive(initialUi())

const options = {
  fundraiserTypes: FUNDRAISER_TYPES,
  durations: DURATIONS,
  currencies: CURRENCIES,
}

function closeAllDropdowns() {
  ui.showTypeOptions = false
  ui.showDurationOptions = false
  ui.showCurrencyOptions = false
  ui.showPhoneCountryOptions = false
}

function toggleReferral() {
  ui.showReferral = !ui.showReferral
}

function toggleTypeOptions() {
  ui.showTypeOptions = !ui.showTypeOptions
  ui.showDurationOptions = false
  ui.showCurrencyOptions = false
  ui.showPhoneCountryOptions = false
}

function toggleDurationOptions() {
  ui.showDurationOptions = !ui.showDurationOptions
  ui.showTypeOptions = false
  ui.showCurrencyOptions = false
  ui.showPhoneCountryOptions = false
}

function toggleCurrencyOptions() {
  ui.showCurrencyOptions = !ui.showCurrencyOptions
  ui.showTypeOptions = false
  ui.showDurationOptions = false
  ui.showPhoneCountryOptions = false
}

function togglePhoneCountryOptions() {
  ui.showPhoneCountryOptions = !ui.showPhoneCountryOptions
  ui.showTypeOptions = false
  ui.showDurationOptions = false
  ui.showCurrencyOptions = false
}

function selectType(option) {
  form.category = option
  ui.showTypeOptions = false
}

function selectDuration(option) {
  form.duration = option
  ui.showDurationOptions = false
}

function syncPhoneMetaFromCurrency(currencyCode) {
  if (ui.phoneMeta.userOverrodeCountry) return

  const mapped = CURRENCY_PHONE_MAP[currencyCode]
  if (!mapped) return

  ui.phoneMeta.countryCode = mapped.countryCode
  ui.phoneMeta.dialCode = mapped.dialCode
  ui.phoneMeta.countryName = mapped.countryName
  ui.phoneMeta.flag = mapped.flag
}

function selectCurrency(code) {
  form.currency = code
  ui.showCurrencyOptions = false
  syncPhoneMetaFromCurrency(code)
}

function setPhoneCountryManually(countryCode, dialCode, countryName, flag) {
  ui.phoneMeta.countryCode = countryCode
  ui.phoneMeta.dialCode = dialCode
  ui.phoneMeta.countryName = countryName
  ui.phoneMeta.flag = flag
  ui.phoneMeta.userOverrodeCountry = true
  ui.showPhoneCountryOptions = false
}

function goToStep(step) {
  if (step < REGISTRATION_STEPS.STEP_1 || step > REGISTRATION_STEPS.TOTAL) {
    return
  }

  ui.currentStep = step
  closeAllDropdowns()
}

function nextStep() {
  if (ui.currentStep < REGISTRATION_STEPS.TOTAL) {
    ui.currentStep += 1
    closeAllDropdowns()
  }
}

function previousStep() {
  if (ui.currentStep > REGISTRATION_STEPS.STEP_1) {
    ui.currentStep -= 1
    closeAllDropdowns()
  }
}

function resetForm() {
  Object.assign(form, initialForm())
  Object.assign(ui, initialUi())
  syncPhoneMetaFromCurrency(form.currency)
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '')
}

function formatNumberWithCommas(value) {
  const digits = digitsOnly(value)
  if (!digits) return ''
  return Number(digits).toLocaleString('en-US')
}

function parseAmount(value) {
  const digits = digitsOnly(value)
  return digits ? Number(digits) : 0
}

function updateTargetAmount(value) {
  form.target = formatNumberWithCommas(value)
}

function getReferralCodeFromCookie(cookieName = 'refCode') {
  if (typeof document === 'undefined') return ''

  const cookies = document.cookie ? document.cookie.split('; ') : []
  const target = cookies.find((row) => row.startsWith(`${cookieName}=`))

  if (!target) return ''

  const rawValue = target.substring(cookieName.length + 1)

  try {
    return decodeURIComponent(rawValue).trim()
  } catch {
    return rawValue.trim()
  }
}

function applyReferralCodeFromCookie() {
  const cookieReferralCode = getReferralCodeFromCookie()

  if (!cookieReferralCode) return

  form.referralCode = cookieReferralCode
  ui.showReferral = true
  ui.referralLocked = true
}

function getAttributionPayload() {
  try {
    const raw = Cookies.get('mch_attrib')
    if (!raw) return null

    const attrib = JSON.parse(decodeURIComponent(raw))

    const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000
    if (attrib.captured_at && Date.now() - attrib.captured_at > MAX_AGE_MS) {
      return null
    }

    return {
      gclid: attrib.gclid || null,
      fbclid: attrib.fbclid || null,
      fbp: attrib.fbp || null,
      utm_source: attrib.utm_source || null,
      utm_medium: attrib.utm_medium || null,
      utm_campaign: attrib.utm_campaign || null,
      utm_term: attrib.utm_term || null,
      utm_content: attrib.utm_content || null,
      captured_at: attrib.captured_at || null,
      landing_host: attrib.landing_host || null,
      landing_path: attrib.landing_path || null,
    }
  } catch {
    return null
  }
}

// initialize once
syncPhoneMetaFromCurrency(form.currency)
applyReferralCodeFromCookie()

export function useRegistrationForm() {
  const progressPercentage = computed(() => {
    return ui.currentStep === REGISTRATION_STEPS.STEP_1 ? 50 : 100
  })

  const currentStepLabel = computed(() => {
    return `Step ${ui.currentStep} of ${REGISTRATION_STEPS.TOTAL}`
  })

  const titleValidation = computed(() => {
    const trimmed = form.title.trim()
    if (!trimmed.length) {
      return { valid: false, message: 'Fundraiser title is required.' }
    }
    if (trimmed.length < 10) {
      return { valid: false, message: 'Minimum 10 characters required.' }
    }
    if (trimmed.length > 34) {
      return { valid: false, message: 'Maximum 34 characters allowed.' }
    }
    return { valid: true, message: '' }
  })

  const categoryValidation = computed(() => {
    if (!form.category) {
      return { valid: false, message: 'Please select a fundraiser type.' }
    }
    if (form.category === 'Other') {
      return { valid: true, warning: 'Political fundraisers NOT allowed.' }
    }
    return { valid: true, message: '' }
  })

  const durationValidation = computed(() => {
    if (!form.duration) {
      return { valid: false, message: 'Please select a duration.' }
    }
    return { valid: true, message: '' }
  })

  const amountValidation = computed(() => {
    const amount = parseAmount(form.target)
    const limits = AMOUNT_LIMITS[form.currency]

    if (!amount) {
      return { valid: false, message: 'Target amount is required.' }
    }

    if (amount < limits.min) {
      return {
        valid: false,
        message: `Minimum target is ${
          form.currency
        } ${limits.min.toLocaleString('en-US')}.`,
      }
    }

    if (amount > limits.max) {
      return {
        valid: false,
        message: `Maximum target is ${limits.max.toLocaleString('en-US')} ${
          form.currency
        }.`,
      }
    }

    return { valid: true, message: '' }
  })

  const emailValidation = computed(() => {
    const trimmed = form.email.trim()

    if (!trimmed.length) {
      return { valid: false, message: 'Email is required.' }
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!emailRegex.test(trimmed)) {
      return { valid: false, message: 'Enter a valid email address.' }
    }

    return { valid: true, message: '' }
  })

  const step1IsValid = computed(() => {
    return (
      titleValidation.value.valid &&
      categoryValidation.value.valid &&
      durationValidation.value.valid &&
      amountValidation.value.valid &&
      emailValidation.value.valid
    )
  })

  // form 2 validations and actions can be added here similarly
  const fullNameValidation = computed(() => {
    const trimmed = form.fullName.trim()

    if (!trimmed.length) {
      return { valid: false, message: 'Full name is required.' }
    }

    if (trimmed.length < 2) {
      return { valid: false, message: 'Enter your full name.' }
    }

    return { valid: true, message: '' }
  })

  const idNumberValidation = computed(() => {
    const trimmed = form.idNumber.trim()

    if (!trimmed.length) {
      return { valid: false, message: 'ID number is required.' }
    }

    if (trimmed.length < 5) {
      return { valid: false, message: 'Enter a valid ID number.' }
    }

    return { valid: true, message: '' }
  })

  const phoneValidation = computed(() => {
    const digits = String(form.phone || '').replace(/\D/g, '')

    if (!digits.length) {
      return { valid: false, message: 'Mobile number is required.' }
    }

    if (
      digits.length < 9 ||
      digits.length > 12 ||
      (digits.length == 10 && digits[0] !== '0')
    ) {
      return { valid: false, message: 'Enter a valid mobile number.' }
    }

    const checkPref = digits.slice(-9)
    if (
      ui.phoneMeta.dialCode == '254' &&
      checkPref[0] != '7' &&
      checkPref[0] != '1'
    ) {
      return { valid: false, message: 'Enter a valid phone number.' }
    } else if (
      ui.phoneMeta.dialCode == '256' &&
      checkPref[0] != '7' &&
      checkPref[0] != '3'
    ) {
      return { valid: false, message: 'Enter a valid phone number.' }
    } else if (
      ui.phoneMeta.dialCode == '255' &&
      checkPref[0] != '7' &&
      checkPref[0] != '6'
    ) {
      return { valid: false, message: 'Phone number is invalid.' }
    }

    return { valid: true, message: '' }
  })

  const birthYearValidation = computed(() => {
    const digits = String(form.birthYear || '').replace(/\D/g, '')
    const currentYear = new Date().getFullYear()

    if (!digits.length) {
      return { valid: false, message: 'Birth year is required.' }
    }

    if (digits.length !== 4) {
      return { valid: false, message: 'Enter a valid 4-digit birth year.' }
    }

    const year = Number(digits)

    if (year < currentYear - 80 || year > currentYear - 18) {
      return {
        valid: false,
        message: 'Enter a valid birth year (18-80 years old).',
      }
    }

    return { valid: true, message: '' }
  })

  const step2IsValid = computed(() => {
    return (
      fullNameValidation.value.valid &&
      idNumberValidation.value.valid &&
      phoneValidation.value.valid &&
      birthYearValidation.value.valid
    )
  })

  return {
    form,
    ui,
    options,
    progressPercentage,
    currentStepLabel,
    validations: {
      emailValidation,
      titleValidation,
      categoryValidation,
      durationValidation,
      amountValidation,
      step1IsValid,
      fullNameValidation,
      idNumberValidation,
      phoneValidation,
      birthYearValidation,
      step2IsValid,
    },
    actions: {
      closeAllDropdowns,
      toggleReferral,
      toggleTypeOptions,
      toggleDurationOptions,
      toggleCurrencyOptions,
      togglePhoneCountryOptions,
      selectType,
      selectDuration,
      selectCurrency,
      syncPhoneMetaFromCurrency,
      setPhoneCountryManually,
      updateTargetAmount,
      goToStep,
      nextStep,
      previousStep,
      resetForm,
      applyReferralCodeFromCookie,
      getAttributionPayload,
    },
  }
}
